/**
 * Frontend mirror of {@code Permissions.java} from server-core.
 *
 * Adding a permission requires adding it in three places:
 *   1. The Flyway seed migration ({@code V1__initial_schema.sql} or a follow-up).
 *   2. {@code Permissions.java} in server-core.
 *   3. This {@code PERM} object.
 *
 * Cross-stack drift is enforced by {@code permissionsDrift.test.ts}, which
 * parses {@code Permissions.java} and asserts the value sets are equal.
 *
 * Keys mirror the backend constant names (UPPER_SNAKE_CASE). Values are the
 * canonical authority strings (e.g. {@code "restaurant.read"}). Sections are
 * ordered alphabetically; within each section, entries are ordered
 * alphabetically by value -- matching {@code Permissions.java}.
 *
 * NOTE: because {@code permissionsDrift.test.ts} asserts this set is *equal* to
 * the backend's, every backend permission must appear here even if no dashboard
 * component references it. A few entries are intentionally FE-unreferenced --
 * they gate backend-only surfaces or drive server-side data scoping:
 *   - {@code system.admin} / {@code tenant.admin} -- backend {@code @PreAuthorize} only
 *     (DLQ console, audit trail); no dedicated dashboard route yet.
 *   - {@code dashboard.platform} -- a data-scope trigger in {@code DashboardScopeResolver},
 *     not an endpoint gate.
 * Do NOT delete these to "clean up" -- removing them breaks the drift test.
 */
export const PERM = {
  // AI
  AI_MENU_UPLOAD: 'ai.menu_upload',

  // AUDIT
  AUDIT_READ: 'audit.read',

  // BILLING
  BILLING_MANAGE: 'billing.manage',
  BILLING_READ: 'billing.read',

  // BOOKINGS
  // booking.* governs every entity on the Booking page: the bookings
  // themselves plus their config entities (rules, sections, schedules,
  // special days).
  BOOKING_CREATE: 'booking.create',
  BOOKING_READ: 'booking.read',
  BOOKING_UPDATE: 'booking.update',
  BOOKING_DELETE: 'booking.delete',

  // COMPLIANCE
  GDPR_EXPORT: 'gdpr.export',

  // DASHBOARD
  DASHBOARD_PLATFORM: 'dashboard.platform',
  DASHBOARD_READ: 'dashboard.read',

  // ERRORS
  ERRORS_MANAGE: 'errors.manage',
  ERRORS_READ: 'errors.read',

  // FLOOR MANAGEMENT (floor.* also governs zones and floor layouts)
  FLOOR_CREATE: 'floor.create',
  FLOOR_DELETE: 'floor.delete',
  FLOOR_READ: 'floor.read',
  FLOOR_UPDATE: 'floor.update',

  // GROUPS
  GROUP_CREATE: 'group.create',
  GROUP_DELETE: 'group.delete',
  GROUP_MANAGE: 'group.manage',
  GROUP_READ: 'group.read',
  GROUP_UPDATE: 'group.update',

  // INVENTORY
  INVENTORY_CREATE: 'inventory.create',
  INVENTORY_DELETE: 'inventory.delete',
  INVENTORY_READ: 'inventory.read',
  INVENTORY_UPDATE: 'inventory.update',

  // MENU (menu.* governs every entity on the Menu page, incl. shifts)
  MENU_CREATE: 'menu.create',
  MENU_DELETE: 'menu.delete',
  MENU_READ: 'menu.read',
  MENU_UPDATE: 'menu.update',

  // PERMISSIONS
  PERMISSION_CREATE: 'permission.create',
  PERMISSION_DELETE: 'permission.delete',
  PERMISSION_READ: 'permission.read',

  // REPORTS
  REPORT_EXPORT: 'report.export',
  REPORT_READ: 'report.read',

  // RESTAURANTS
  RESTAURANT_CREATE: 'restaurant.create',
  RESTAURANT_DELETE: 'restaurant.delete',
  RESTAURANT_MANAGE: 'restaurant.manage',
  RESTAURANT_PROVISION: 'restaurant.provision',
  RESTAURANT_READ: 'restaurant.read',
  RESTAURANT_UPDATE: 'restaurant.update',

  // ROLES
  ROLE_CREATE: 'role.create',
  ROLE_DELETE: 'role.delete',
  ROLE_READ: 'role.read',
  ROLE_UPDATE: 'role.update',

  // SUBSCRIPTIONS
  SUBSCRIPTION_MANAGE: 'subscription.manage',
  SUBSCRIPTION_READ: 'subscription.read',

  // SYNC
  SYNC_MANAGE: 'sync.manage',
  SYNC_READ: 'sync.read',
  SYNC_RESOLVE_CONFLICTS: 'sync.resolve_conflicts',
  SYNC_TRIGGER: 'sync.trigger',

  // SYSTEM
  SYSTEM_ADMIN: 'system.admin',
  TENANT_ADMIN: 'tenant.admin',

  // TABLES (physical tables are managed under floor.*; only live-status
  // monitoring -- the live-tables view, table status flag changes, and the
  // TableStatus entity -- is gated separately by table.monitor)
  TABLE_MONITOR: 'table.monitor',

  // TERMINALS
  TERMINAL_CREATE: 'terminal.create',
  TERMINAL_DELETE: 'terminal.delete',
  TERMINAL_HEARTBEAT: 'terminal.heartbeat',
  TERMINAL_MANAGE: 'terminal.manage',
  TERMINAL_READ: 'terminal.read',
  TERMINAL_UPDATE: 'terminal.update',

  // USERS
  USER_CREATE: 'user.create',
  USER_DELETE: 'user.delete',
  USER_READ: 'user.read',
  USER_UPDATE: 'user.update',
} as const;

/**
 * Union of all canonical permission strings. Use this as the type for
 * permission-name parameters when callers should be limited to known
 * permissions at compile time. Note: {@link useHasPermission} accepts an
 * arbitrary {@code string} so callers can pass dynamic values too.
 */
export type Permission = (typeof PERM)[keyof typeof PERM];
